import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { createWikiPage, updateWikiPage, uploadWikiImage } from 'api';
import { useWikiPage, MarkdownContent } from 'features/wiki';

const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const WikiPageFormPage = () => {
  const { pageId } = useParams();
  const isEditing = Boolean(pageId);
  const { page, loading } = useWikiPage(pageId);
  const { pages, refreshWikiPages } = useOutletContext();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [parentId, setParentId] = useState('');
  const [tab, setTab] = useState('write');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const parentOptions = useMemo(
    () => (pages || []).filter((p) => !p.parentId && p.id !== pageId),
    [pages, pageId],
  );

  useEffect(() => {
    if (isEditing && page) {
      setTitle(page.title);
      setContent(page.content);
      setParentId(page.parentId || '');
    }
  }, [isEditing, page]);

  const handleInsertImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Unsupported image type. Allowed types: PNG, JPEG, GIF, WebP');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError('Image size must not exceed 5MB');
      return;
    }

    setError(null);
    const textarea = textareaRef.current;
    const isFocused = textarea && document.activeElement === textarea;

    try {
      setUploadingImage(true);
      const { url } = await uploadWikiImage(file);
      const absoluteUrl = `${process.env.REACT_APP_API_URL}${url}`;
      const markdown = `![](${absoluteUrl})`;

      setContent((prevContent) => {
        const start = isFocused ? textarea.selectionStart : prevContent.length;
        const end = isFocused ? textarea.selectionEnd : prevContent.length;
        const nextContent = `${prevContent.slice(0, start)}${markdown}${prevContent.slice(end)}`;
        requestAnimationFrame(() => {
          if (textarea) {
            textarea.focus();
            const cursor = start + markdown.length;
            textarea.setSelectionRange(cursor, cursor);
          }
        });
        return nextContent;
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      setSubmitting(true);
      const payload = { title: title.trim(), content, parentId: parentId || null };
      const saved = isEditing ? await updateWikiPage(pageId, payload) : await createWikiPage(payload);
      await refreshWikiPages();
      navigate(`/wiki/${saved.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (isEditing && loading) {
    return (
      <div className="gh-box">
        <div className="gh-box-body d-flex align-items-center gap-2 text-muted-gh">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="gh-box">
      <div className="gh-box-header">
        <i className="bi bi-pencil-square me-2" aria-hidden="true"></i>
        {isEditing ? 'Edit page' : 'New page'}
      </div>
      <div className="gh-box-body">
        {error && (
          <div className="alert alert-danger d-flex align-items-center gap-2" role="alert">
            <i className="bi bi-exclamation-octagon-fill" aria-hidden="true"></i>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="wiki-title" className="form-label text-muted-gh">Title</label>
            <input
              id="wiki-title"
              type="text"
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="mb-3">
            <label htmlFor="wiki-parent" className="form-label text-muted-gh">Parent page</label>
            <select
              id="wiki-parent"
              className="form-select"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">(No parent - top-level page)</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </div>

          <div className="mb-2 d-flex justify-content-between align-items-end">
            <label className="form-label text-muted-gh d-block mb-0">Content (Markdown)</label>
            <button
              type="button"
              className="btn btn-gh btn-sm"
              onClick={handleInsertImageClick}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="bi bi-image me-1" aria-hidden="true"></i>
                  Insert image
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              className="d-none"
              onChange={handleImageFileSelected}
            />
          </div>
          <ul className="nav nav-tabs mb-2">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${tab === 'write' ? ' active' : ''}`}
                onClick={() => setTab('write')}
              >
                Write
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${tab === 'preview' ? ' active' : ''}`}
                onClick={() => setTab('preview')}
              >
                Preview
              </button>
            </li>
          </ul>

          {tab === 'write' ? (
            <textarea
              ref={textareaRef}
              className="form-control wiki-editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={16}
              required
            />
          ) : (
            <div className="wiki-editor-preview">
              <MarkdownContent content={content} />
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button
              type="button"
              className="btn btn-gh"
              onClick={() => navigate(isEditing ? `/wiki/${pageId}` : '/', { replace: true })}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-gh-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WikiPageFormPage;
